<template>
  <div id="delivery-report">

    <div v-if="initProgress" class="w-100 mt-5 mb-5">
      <vs-progress indeterminate color="primary"></vs-progress>
    </div>

    <vx-card collapse="false" ref="filterCard" title="Filtros" class="mb-8" collapse-action
             refresh-content-action @refresh="resetFilters">
      <div class="vx-row mb-4">
        <div class="vx-col md:w-1/2">
          <h6 class="mt-2 mb-2">Cliente (opcional)</h6>
          <v-select
            :options="clients"
            :clearable="true"
            :dir="$vs.rtl ? 'rtl' : 'ltr'"
            v-model="client"
            placeholder="Todos los clientes"
            class="mb-4 md:mb-0"
          />
        </div>
      </div>
      <div class="vx-row">
        <div class="vx-col md:w-1/2">
          <flat-pickr :config="configdateTimePicker"
                      v-model="filterData.startDate"
                      placeholder="Desde"
                      class="w-full"/>
        </div>
        <div class="vx-col md:w-1/2">
          <flat-pickr :config="configdateTimePicker"
                      v-model="filterData.endDate"
                      placeholder="Hasta"
                      class="w-full"/>
        </div>
        <div class="vx-col mt-4 flex items-center">
          <vs-button @click="fetchData" class="mr-4">Filtrar</vs-button>
          <vs-button @click="exportToExcel" type="border" :disabled="list.length === 0">Exportar Excel</vs-button>
        </div>
      </div>
    </vx-card>

    <!-- Empty / Loading state -->
    <div v-if="!hasFetched" class="text-center py-16 text-gray-500 text-sm">
      Selecciona un rango de fechas y presiona <strong>Filtrar</strong> para generar el reporte.
    </div>
    <div v-else-if="list.length === 0" class="text-center py-16 text-gray-500 text-sm">
      No se encontraron envíos para el rango seleccionado.
    </div>

    <!-- RESUMEN POR TIENDA (solo cuando no se filtra por cliente) -->
    <div v-if="list.length > 0 && !client" class="max-w-full overflow-auto mb-8">
      <div class="flex justify-end mb-2">
        <vs-button @click="exportSummaryToExcel" type="border" size="medium">Exportar Resumen</vs-button>
      </div>
      <table class="w-full border-collapse text-xs">
        <caption style="background:#1F3864; color:#fff;" class="py-2 font-bold text-sm tracking-wide">RESUMEN POR TIENDA</caption>
        <thead>
          <tr style="background:#1F3864; color:#fff;" class="text-center">
            <th class="border border-white px-3 py-2 text-left">TIENDA</th>
            <th class="border border-white px-3 py-2 text-left">CUENTAS</th>
            <th class="border border-white px-3 py-2">DESDE</th>
            <th class="border border-white px-3 py-2">HASTA</th>
            <th class="border border-white px-3 py-2">TOTAL X ABONAR (S/.)</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, idx) in clientSummary" :key="row.clientId"
            :class="idx % 2 === 0 ? 'bg-white' : 'bg-gray-100'"
            class="border-b-2 border-gray-300">
            <td class="border border-gray-300 px-4 py-3 font-bold">{{ row.clientName }}</td>
            <td class="border border-gray-300 px-4 py-3 text-left" style="white-space: pre-line;">{{ row.accounts }}</td>
            <td class="border border-gray-300 px-4 py-3 text-center">{{ formatDateDisplay(filterData.startDate) }}</td>
            <td class="border border-gray-300 px-4 py-3 text-center">{{ formatDateDisplay(filterData.endDate) }}</td>
            <td class="border border-gray-300 px-4 py-3 text-center font-bold"
              :class="row.total < 0 ? 'bg-red-100 text-red-700' : row.total > 0 ? 'text-green-800' : 'text-gray-500'">
              {{ row.total.toFixed(2) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- REPORTE DE ENVIOS (solo con cliente seleccionado) -->
    <div v-if="list.length > 0 && client" class="max-w-full overflow-auto mb-8">
      <table class="w-full border-collapse text-xs">
        <caption style="background:#1F3864; color:#fff;" class="py-2 font-bold text-sm tracking-wide">REPORTE DE ENVIOS</caption>
        <thead>
          <tr style="background:#1F3864; color:#fff;" class="text-center">
            <th class="border border-white px-3 py-2 text-left">FECHA</th>
            <th class="border border-white px-3 py-2">TOTAL DE ENVIOS</th>
            <th class="border border-white px-3 py-2">ENTREGADOS</th>
            <th class="border border-white px-3 py-2">REPROGRAMADOS</th>
            <th class="border border-white px-3 py-2">RECHAZADOS</th>
            <th class="border border-white px-3 py-2">RECHAZADOS CON COBRO</th>
            <th class="border border-white px-3 py-2">ANULADOS</th>
            <th class="border border-white px-3 py-2">ANULADOS CON COBRO</th>
            <th class="border border-white px-3 py-2">EFECTIVIDAD DEL SERVICIO (%)</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(group, date) in groupedByDate" :key="date" class="border border-gray-300">
            <td class="border border-gray-300 px-3 py-2 font-bold">{{ formatDateDisplay(date) }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ group.length }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ countByStatus(group, 'done') }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ countRescheduled(group) }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ countByStatus(group, 'rejected') }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ countByStatus(group, 'rejectedWithCharge') }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ countByStatus(group, 'canceled') }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ countByStatus(group, 'canceledWithCharge') }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center font-bold"
              :class="calcEfficiency(group) >= 100 ? 'bg-yellow-200 text-yellow-700' : calcEfficiency(group) >= 80 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'">
              {{ calcEfficiency(group) }}%
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- REPORTE DE FINANZAS (solo con cliente seleccionado) -->
    <div v-if="list.length > 0 && client" class="max-w-full overflow-auto mb-8">
      <table class="w-full border-collapse text-xs">
        <caption style="background:#1F3864; color:#fff;" class="py-2 font-bold text-sm tracking-wide">REPORTE DE FINANZAS</caption>
        <thead>
          <tr style="background:#1F3864; color:#fff;" class="text-center">
            <th class="border border-white px-3 py-2" rowspan="2" style="vertical-align:middle; text-align:left">FECHA</th>
            <th class="border border-white px-3 py-2" colspan="3">TOTAL SERVICIO DE ENWAM (-)</th>
            <th class="border border-white px-3 py-2" colspan="3">TOTAL DINERO DE TIENDA (+)</th>
            <th class="border border-white px-3 py-2" rowspan="2" style="vertical-align:middle">TOTAL (S/.)</th>
            <th class="border border-white px-3 py-2" rowspan="2" style="vertical-align:middle">ESTADO</th>
          </tr>
          <tr style="background:#1F3864; color:#fff;" class="text-center">
            <th class="border border-white px-3 py-2">COSTO DE RECOJO<br><span class="font-normal">(s/.10)</span></th>
            <th class="border border-white px-3 py-2">ADICIONALES<br><span class="font-normal">(Tamaño, Peso, otros) s/.</span></th>
            <th class="border border-white px-3 py-2">TARIFA TOTAL (s/.)</th>
            <th class="border border-white px-3 py-2">EN CUENTAS ENWAM<br><span class="font-normal">(s/.)</span></th>
            <th class="border border-white px-3 py-2">EN EFECTIVO<br><span class="font-normal">(s/.)</span></th>
            <th class="border border-white px-3 py-2">PAGOS POR SISTEMA<br><span class="font-normal">(s/.)</span></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(group, date) in groupedByDate" :key="date" class="border border-gray-300">
            <td class="border border-gray-300 px-3 py-2 font-bold">{{ formatDateDisplay(date) }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ financeOf(group, date).costoRecojo }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ financeOf(group, date).adicionales }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ financeOf(group, date).tarifaTotal }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ financeOf(group, date).enCuentasEnwam }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ financeOf(group, date).enEfectivo }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ financeOf(group, date).pagosSistema }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center font-bold"
              :class="parseFloat(financeOf(group, date).total) < 0 ? 'text-red-700' : 'text-green-800'">
              {{ financeOf(group, date).total }}
            </td>
            <td class="border border-gray-300 px-3 py-2 text-center font-bold"
              :class="getEstadoClass(financeOf(group, date).total)">
              <span>{{ getEstadoText(financeOf(group, date).total) }}</span>
              <!-- Estado: "Debe" y cliente subió voucher pendiente => Aprobar / Rechazar -->
              <div v-if="parseFloat(financeOf(group, date).total) < 0 && getPendingPayments(date).length > 0" class="mt-1">
                <div v-for="payment in getPendingPayments(date)" :key="payment.id" class="mb-1">
                  <a :href="payment.voucherUrl" target="_blank" class="text-blue-600 underline text-xs block mb-1">Ver voucher</a>
                  <vs-button size="small" color="success" @click="approvePayment(payment)" class="mr-1" style="font-size:10px;">Aprobar</vs-button>
                  <vs-button size="small" color="danger" @click="rejectPayment(payment)" style="font-size:10px;">Rechazar</vs-button>
                </div>
              </div>
              <!-- Estado: "Debe" y sin voucher pendiente => admin puede subir -->
              <div v-else-if="parseFloat(financeOf(group, date).total) < 0" class="mt-1">
                <vs-button size="small" color="primary" @click="openPaymentModal(date, parseFloat(financeOf(group, date).total))" style="font-size:10px;">
                  Subir voucher
                </vs-button>
              </div>
              <!-- Estado: "Por abonar" => Subir voucher (auto-aprobado) -->
              <div v-if="parseFloat(financeOf(group, date).total) > 0" class="mt-1">
                <vs-button size="small" color="primary" @click="openPaymentModal(date, parseFloat(financeOf(group, date).total))" style="font-size:10px;">
                  Subir voucher
                </vs-button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- REPORTE DETALLADO DE ENVIOS (solo con cliente seleccionado) -->
    <div v-if="list.length > 0 && client" class="max-w-full overflow-auto mb-8">
      <table class="w-full border-collapse text-xs">
        <caption style="background:#1F3864; color:#fff;" class="py-2 font-bold text-sm tracking-wide">REPORTE DETALLADO DE ENVIOS</caption>
        <thead>
          <tr style="background:#C00000; color:#fff;" class="text-center">
            <th class="border border-white px-2 py-2">COD TRACKING</th>
            <th class="border border-white px-2 py-2">FECHA</th>
            <th class="border border-white px-2 py-2">TIENDA</th>
            <th class="border border-white px-2 py-2">CLIENTE</th>
            <th class="border border-white px-2 py-2">TIPO DE SERVICIO</th>
            <th class="border border-white px-2 py-2">DISTRITO</th>
            <th class="border border-white px-2 py-2">COSTO DE RECOJO<br><span class="font-normal">(S/.10)</span></th>
            <th class="border border-white px-2 py-2">ADICIONAL O PAGO EN AGENCIA</th>
            <th class="border border-white px-2 py-2">TARIFA</th>
            <th class="border border-white px-2 py-2">TOTAL A COBRAR</th>
            <th class="border border-white px-2 py-2">DETALLE DE COBRO</th>
            <th class="border border-white px-2 py-2">TIPO DE PAGO</th>
            <th class="border border-white px-2 py-2">INGRESO A CUENTAS</th>
            <th class="border border-white px-2 py-2">EFECTIVO</th>
            <th class="border border-white px-2 py-2">ESTADO</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(o, index) in list" :key="index" class="border border-gray-200 hover:bg-gray-50">
            <td class="border border-gray-200 px-2 py-1 whitespace-no-wrap">{{ o.correlative }}</td>
            <td class="border border-gray-200 px-2 py-1 whitespace-no-wrap">{{ formatDateDisplay(o.pickUpDate) }}</td>
            <td class="border border-gray-200 px-2 py-1 whitespace-no-wrap">{{ o.client ? o.client.name : '' }}</td>
            <td class="border border-gray-200 px-2 py-1">{{ o.fullName }}</td>
            <td class="border border-gray-200 px-2 py-1 whitespace-no-wrap">{{ o.type ? o.type.label : '' }}</td>
            <td class="border border-gray-200 px-2 py-1 whitespace-no-wrap">{{ o.district ? o.district.label : '' }}</td>
            <td class="border border-gray-200 px-2 py-1 text-center">{{ getCostoRecojoPorEnvio(o) }}</td>
            <td class="border border-gray-200 px-2 py-1 text-center">{{ o.extraCost > 0 ? o.extraCost : '' }}</td>
            <td class="border border-gray-200 px-2 py-1 text-center whitespace-no-wrap">S/.{{ ((o.district ? o.district.price : 0) + (o.type ? o.type.price || 0 : 0)).toFixed(2) }}</td>
            <td class="border border-gray-200 px-2 py-1 text-center">{{ o.totalToBeCollected > 0 ? o.totalToBeCollected : '' }}</td>
            <td class="border border-gray-200 px-2 py-1">{{ getDetalleCobro(o) }}</td>
            <td class="border border-gray-200 px-2 py-1 whitespace-no-wrap">{{ getTipoPago(o) }}</td>
            <td class="border border-gray-200 px-2 py-1 text-center whitespace-no-wrap">S/.{{ getIngresoACuentas(o).toFixed(2) }}</td>
            <td class="border border-gray-200 px-2 py-1 text-center whitespace-no-wrap">S/.{{ getEfectivo(o).toFixed(2) }}</td>
            <td class="border border-gray-200 px-2 py-1 whitespace-no-wrap">
              <vs-chip :color="getStatusColor(o.status)">{{ getStatusText(o.status) }}</vs-chip>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Payment voucher modal (for "Por abonar" - auto approved) -->
    <vs-popup title="Subir voucher de pago" :active.sync="showPaymentModal">
      <div class="mb-4">
        <p class="text-sm text-gray-600 mb-1">Fecha: <strong>{{ formatDateDisplay(paymentData.debtDate) }}</strong></p>
        <p class="text-sm text-gray-600">Monto: <strong class="text-green-700">S/.{{ Math.abs(paymentData.amount).toFixed(2) }}</strong></p>
      </div>
      <div class="mb-4">
        <label class="block text-sm font-medium mb-1">Voucher de pago</label>
        <input type="file" accept="image/*" @change="onVoucherFileChange" class="block w-full text-sm" />
        <img v-if="paymentData.previewUrl" :src="paymentData.previewUrl" class="mt-2 max-h-48 rounded border" />
      </div>
      <div class="flex justify-end gap-2">
        <vs-button @click="showPaymentModal = false" type="border">Cancelar</vs-button>
        <vs-button @click="submitPayment" :disabled="!paymentData.file || paymentData.uploading">
          {{ paymentData.uploading ? 'Enviando...' : 'Enviar voucher' }}
        </vs-button>
      </div>
    </vs-popup>

  </div>
</template>

<script>
import moment from 'moment'
import vSelect from 'vue-select'
import { db, Timestamp, storage } from '@/firebase/firebaseConfig'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'

export default {
  name: 'DeliveryReport',
  components: { vSelect, flatPickr },
  data() {
    return {
      list: [],
      clients: [],
      client: null,
      payments: {},
      hasFetched: false,
      initProgress: false,
      showPaymentModal: false,
      paymentData: {
        debtDate: '',
        amount: 0,
        clientId: '',
        file: null,
        previewUrl: null,
        uploading: false
      },
      filterData: {
        startDate: moment(new Date()).format('YYYY-MM-DD'),
        endDate: moment(new Date()).format('YYYY-MM-DD')
      },
      configdateTimePicker: {
        dateFormat: 'Y-m-d'
      }
    }
  },
  async created() {
    try {
      await this.loadClients()
    } catch (e) {
      this.$vs.notify({ color: 'warning', title: '¡Oh no!', text: 'Hubo un error al cargar clientes.' })
    }
  },
  computed: {
    groupedByDate() {
      const groups = {}
      this.list.forEach(o => {
        const key = o.pickUpDate
        if (!groups[key]) groups[key] = []
        groups[key].push(o)
      })
      return Object.keys(groups).sort().reduce((acc, key) => {
        acc[key] = groups[key]
        return acc
      }, {})
    },
    clientSummary() {
      // Group shipments by clientId
      const byClient = {}
      this.list.forEach(o => {
        const cid = o.client?.id
        if (!cid) return
        if (!byClient[cid]) {
          byClient[cid] = { clientId: cid, clientName: o.client.name || '', shipments: [] }
        }
        byClient[cid].shipments.push(o)
      })

      const chargeableStatuses = ['done', 'rescheduledWithCharge', 'canceledWithCharge', 'rejectedWithCharge']

      return Object.values(byClient).map(entry => {
        const clientData = this.clients.find(c => c.id === entry.clientId)
        const accounts = clientData ? clientData.accounts : ''

        // Group shipments by date for costoRecojo calculation
        const byDate = {}
        entry.shipments.forEach(o => {
          if (!byDate[o.pickUpDate]) byDate[o.pickUpDate] = []
          byDate[o.pickUpDate].push(o)
        })

        let rawTotal = 0
        Object.entries(byDate).forEach(([, group]) => {
          const costoRecojo = group.length === 1 ? 10 : 0
          let adicionales = 0
          let tarifaTotal = 0
          let pagosSistema = 0

          group.forEach(o => {
            const isChargeable = chargeableStatuses.includes(o.status)
            if (isChargeable) {
              adicionales += o.extraCost || 0
              tarifaTotal += (o.district ? o.district.price || 0 : 0) + (o.type ? o.type.price || 0 : 0)
            }
            pagosSistema += (o.district ? o.district.price || 0 : 0) + (o.type ? o.type.price || 0 : 0)
          })

          rawTotal += pagosSistema - tarifaTotal - adicionales - costoRecojo
        })

        // Discount approved payments for this client
        let approvedTotal = 0
        Object.keys(this.payments).forEach(key => {
          if (key.startsWith(entry.clientId + '_')) {
            const datePayments = this.payments[key] || []
            approvedTotal += datePayments
              .filter(p => p.status === 'approved')
              .reduce((sum, p) => sum + (p.amount || 0), 0)
          }
        })

        return {
          clientId: entry.clientId,
          clientName: entry.clientName,
          accounts,
          total: rawTotal + approvedTotal
        }
      }).sort((a, b) => a.clientName.localeCompare(b.clientName))
    }
  },
  methods: {
    async loadClients() {
      const snap = await db.collection('clients').orderBy('createdAt', 'desc').get()
      snap.forEach(doc => {
        this.clients.push({
          id: doc.id,
          label: doc.data().businessName + ' - ' + doc.data().document,
          name: doc.data().businessName,
          accounts: doc.data().accounts || ''
        })
      })
    },

    async fetchData() {
      try {
        // Validate max 7 days when no client selected
        if (!this.client) {
          const start = new Date(this.filterData.startDate)
          const end = new Date(this.filterData.endDate)
          const diffDays = Math.round((end - start) / (1000 * 60 * 60 * 24))
          if (diffDays > 7) {
            this.$vs.notify({ color: 'warning', title: 'Rango excedido', text: 'Sin cliente seleccionado, el rango máximo es de 7 días.' })
            return
          }
        }

        this.initProgress = true
        this.list = []

        const startDate = new Date(this.filterData.startDate + ' 05:00:00')
        startDate.setHours(0, 0, 0, 0)
        const endDate = new Date(this.filterData.endDate + ' 05:00:00')
        endDate.setHours(23, 59, 59, 0)

        const startTs = Timestamp.fromDate(startDate)
        const endTs = Timestamp.fromDate(endDate)

        let query = db.collection('detail')
          .where('deadline', '>=', startTs)
          .where('deadline', '<=', endTs)

        if (this.client) {
          query = query.where('client.id', '==', this.client.id)
        }

        query = query.orderBy('deadline', 'asc')

        const snap = await query.get()

        snap.forEach(doc => {
          const data = doc.data()
          const pickUpDate = data.pickUpDate
            ? (typeof data.pickUpDate.toDate === 'function'
              ? moment(data.pickUpDate.toDate()).format('YYYY-MM-DD')
              : data.pickUpDate)
            : ''
          const deadline = data.deadline
            ? (typeof data.deadline.toDate === 'function'
              ? moment(data.deadline.toDate()).format('YYYY-MM-DD')
              : data.deadline)
            : ''

          this.list.push({
            id: doc.id,
            ...data,
            pickUpDate,
            deadline,
            status: data.status || 'hold',
            extraCost: data.extraCost || 0
          })
        })

        await this.fetchPayments()
        this.hasFetched = true
      } catch (e) {
        console.error(e)
        this.$vs.notify({ color: 'warning', title: '¡Oh no!', text: 'Error al cargar el reporte.' })
      } finally {
        this.initProgress = false
      }
    },

    async fetchPayments() {
      this.payments = {}

      const clientIds = [...new Set(this.list.map(o => o.client?.id).filter(Boolean))]

      // Collect all dates from the list for filtering
      const dates = [...new Set(this.list.map(o => o.pickUpDate).filter(Boolean))]

      // Fetch all clients' payments in parallel
      const promises = clientIds.map(clientId =>
        db.collection('clients').doc(clientId)
          .collection('payments')
          .where('debtDate', 'in', dates.length > 0 ? dates.slice(0, 10) : ['__none__'])
          .get()
          .then(snap => ({ clientId, snap }))
      )

      // If more than 10 unique dates, fallback to fetching all and filtering locally
      const useDateFilter = dates.length <= 10
      const fallbackPromises = !useDateFilter
        ? clientIds.map(clientId =>
            db.collection('clients').doc(clientId)
              .collection('payments')
              .get()
              .then(snap => ({ clientId, snap }))
          )
        : []

      const results = await Promise.all(useDateFilter ? promises : fallbackPromises)
      const dateSet = new Set(dates)

      results.forEach(({ clientId, snap }) => {
        snap.forEach(doc => {
          const data = doc.data()
          if (!useDateFilter && !dateSet.has(data.debtDate)) return
          const key = clientId + '_' + data.debtDate
          if (!this.payments[key]) this.payments[key] = []
          this.payments[key].push({ id: doc.id, clientId, ...data })
        })
      })
    },

    getPaymentsForDate(date) {
      // When filtered by client, use that client; otherwise aggregate all clients for that date
      const clientIds = this.client
        ? [this.client.id]
        : [...new Set(this.list.filter(o => o.pickUpDate === date).map(o => o.client?.id).filter(Boolean))]

      let allPayments = []
      for (const cid of clientIds) {
        const key = cid + '_' + date
        allPayments = allPayments.concat(this.payments[key] || [])
      }
      return allPayments
    },

    getApprovedPayments(date) {
      return this.getPaymentsForDate(date)
        .filter(p => p.status === 'approved')
        .reduce((sum, p) => sum + (p.amount || 0), 0)
    },

    getPendingPayments(date) {
      return this.getPaymentsForDate(date).filter(p => p.status === 'pending')
    },

    async approvePayment(payment) {
      try {
        await db.collection('clients').doc(payment.clientId)
          .collection('payments').doc(payment.id)
          .update({ status: 'approved' })

        this.$vs.notify({ color: 'success', title: 'Aprobado', text: 'El voucher ha sido aprobado.' })
        await this.fetchPayments()
        this.$forceUpdate()
      } catch (e) {
        console.error(e)
        this.$vs.notify({ color: 'warning', title: '¡Oh no!', text: 'Error al aprobar el voucher.' })
      }
    },

    async rejectPayment(payment) {
      try {
        await db.collection('clients').doc(payment.clientId)
          .collection('payments').doc(payment.id)
          .update({ status: 'rejected' })

        this.$vs.notify({ color: 'success', title: 'Rechazado', text: 'El voucher ha sido rechazado.' })
        await this.fetchPayments()
        this.$forceUpdate()
      } catch (e) {
        console.error(e)
        this.$vs.notify({ color: 'warning', title: '¡Oh no!', text: 'Error al rechazar el voucher.' })
      }
    },

    openPaymentModal(date, amount) {
      // For "Por abonar", we need to identify which client(s) are involved
      const clientIds = this.client
        ? [this.client.id]
        : [...new Set(this.list.filter(o => o.pickUpDate === date).map(o => o.client?.id).filter(Boolean))]

      this.paymentData = {
        debtDate: date,
        amount: Math.abs(amount),
        clientId: clientIds[0] || '',
        file: null,
        previewUrl: null,
        uploading: false
      }
      this.showPaymentModal = true
    },

    onVoucherFileChange(e) {
      const file = e.target.files[0]
      if (!file) return
      this.paymentData.file = file
      this.paymentData.previewUrl = URL.createObjectURL(file)
    },

    async submitPayment() {
      try {
        this.paymentData.uploading = true
        const clientId = this.paymentData.clientId
        if (!clientId) {
          this.$vs.notify({ color: 'warning', title: '¡Oh no!', text: 'No se pudo identificar el cliente.' })
          return
        }

        const file = this.paymentData.file
        const fileName = `payments/${clientId}/${Date.now()}_${file.name}`

        const ref = storage.ref(fileName)
        await ref.put(file)
        const voucherUrl = await ref.getDownloadURL()

        // Auto-approve since it's from back office
        await db.collection('clients').doc(clientId)
          .collection('payments').add({
            clientId,
            debtDate: this.paymentData.debtDate,
            amount: this.paymentData.amount,
            voucherUrl,
            status: 'approved',
            createdAt: Timestamp.now()
          })

        this.$vs.notify({ color: 'success', title: 'Voucher enviado', text: 'Voucher subido y aprobado automáticamente.' })
        this.showPaymentModal = false
        await this.fetchPayments()
        this.$forceUpdate()
      } catch (e) {
        console.error(e)
        this.$vs.notify({ color: 'warning', title: '¡Oh no!', text: 'Error al enviar voucher.' })
      } finally {
        this.paymentData.uploading = false
      }
    },

    async resetFilters() {
      this.client = null
      this.filterData.startDate = moment(new Date()).format('YYYY-MM-DD')
      this.filterData.endDate = moment(new Date()).format('YYYY-MM-DD')
      this.list = []
      this.payments = {}
      this.hasFetched = false
      this.$refs.filterCard.removeRefreshAnimation()
    },

    countByStatus(group, status) {
      return group.filter(o => o.status === status).length
    },

    countRescheduled(group) {
      return group.filter(o => ['rescheduledWithoutCharge', 'rescheduledWithCharge'].includes(o.status)).length
    },

    calcEfficiency(group) {
      if (!group.length) return 0
      const delivered = this.countByStatus(group, 'done')
      return Math.round((delivered / group.length) * 100)
    },

    financeOf(group, date) {
      const costoRecojo = group.length === 1 ? 10 : 0
      let adicionales = 0
      let tarifaTotal = 0
      let enCuentasEnwam = 0
      let enEfectivo = 0
      let pagosSistema = 0

      const chargeableStatuses = ['done', 'rescheduledWithCharge', 'canceledWithCharge', 'rejectedWithCharge']

      group.forEach(o => {
        const isChargeable = chargeableStatuses.includes(o.status)

        if (isChargeable) {
          adicionales += o.extraCost || 0
          tarifaTotal += (o.district ? o.district.price || 0 : 0) + (o.type ? o.type.price || 0 : 0)
        }

        pagosSistema += (o.district ? o.district.price || 0 : 0) + (o.type ? o.type.price || 0 : 0)

        const motive = o.dealer ? o.dealer.motive || '' : ''
        const amount = o.totalToBeCollected || 0
        if (motive === 'A Enwam') {
          enCuentasEnwam += amount
        } else if (motive === 'Efectivo') {
          enEfectivo += amount
        } else if (motive === 'Pago Mixto') {
          const mixed = this.getMixedPaymentBreakdown(o)
          enCuentasEnwam += mixed.accounts
          enEfectivo += mixed.cash
        }
        // "A Tienda" → store already received it, nothing to add
      })

      const rawTotal = pagosSistema - tarifaTotal - adicionales - costoRecojo
      const approvedPayments = date ? this.getApprovedPayments(date) : 0
      const total = rawTotal + approvedPayments

      return {
        costoRecojo: costoRecojo.toFixed(1),
        adicionales: adicionales.toFixed(1),
        tarifaTotal: tarifaTotal.toFixed(1),
        enCuentasEnwam: enCuentasEnwam.toFixed(1),
        enEfectivo: enEfectivo.toFixed(1),
        pagosSistema: pagosSistema.toFixed(1),
        rawTotal: rawTotal.toFixed(2),
        total: total.toFixed(2)
      }
    },

    getCostoRecojoPorEnvio(o) {
      const sameDay = this.list.filter(s => s.pickUpDate === o.pickUpDate)
      return sameDay.length === 1 ? 10 : ''
    },

    getDetalleCobro(o) {
      return o.dealer ? o.dealer.motive || '-' : '-'
    },

    getTipoPago(o) {
      return o.dealer ? o.dealer.accountDetails || '-' : '-'
    },

    getMixedPaymentBreakdown(o) {
      const dealer = o.dealer || {}
      const m1Method = dealer.mixedPayment1Method || ''
      const m1Amount = parseFloat(dealer.mixedPayment1Amount) || 0
      const m2Method = dealer.mixedPayment2Method || ''
      const m2Amount = parseFloat(dealer.mixedPayment2Amount) || 0

      let accounts = 0
      let cash = 0

      // Method 1: store methods are ignored, cash goes to cash, rest to Enwam accounts
      if (m1Method.toLowerCase().includes('tienda')) { /* store received it */ }
      else if (m1Method === 'Efectivo') cash += m1Amount
      else if (m1Amount > 0) accounts += m1Amount

      // Method 2
      if (m2Method.toLowerCase().includes('tienda')) { /* store received it */ }
      else if (m2Method === 'Efectivo') cash += m2Amount
      else if (m2Amount > 0) accounts += m2Amount

      return { accounts, cash }
    },

    getIngresoACuentas(o) {
      if (!o.dealer) return 0
      const motive = o.dealer.motive || ''
      if (motive === 'A Enwam') return o.totalToBeCollected || 0
      if (motive === 'Pago Mixto') return this.getMixedPaymentBreakdown(o).accounts
      return 0
    },

    getEfectivo(o) {
      if (!o.dealer) return 0
      const motive = o.dealer.motive || ''
      if (motive === 'Efectivo') return o.totalToBeCollected || 0
      if (motive === 'Pago Mixto') return this.getMixedPaymentBreakdown(o).cash
      return 0
    },

    getEstadoText(total) {
      const t = parseFloat(total)
      if (t > 0) return 'Por abonar'
      if (t === 0) return 'Sin deuda'
      return 'Debe'
    },

    getEstadoClass(total) {
      const t = parseFloat(total)
      if (t > 0) return 'text-green-700'
      if (t === 0) return 'text-gray-500'
      return 'text-red-700'
    },

    getStatusText(status) {
      const map = {
        hold: 'Sin confirmar',
        done: 'Entregado',
        canceled: 'Anulado',
        canceledWithCharge: 'Anulado con cobro',
        rejected: 'Rechazado',
        rejectedWithCharge: 'Rechazado con cobro',
        rescheduledWithoutCharge: 'Reprogramado sin cobro',
        rescheduledWithCharge: 'Reprogramado con cobro'
      }
      return map[status] || status
    },

    getStatusColor(status) {
      const map = {
        hold: 'dark',
        done: 'success',
        canceled: 'dark',
        canceledWithCharge: 'dark',
        rejected: 'danger',
        rejectedWithCharge: 'danger',
        rescheduledWithoutCharge: 'warning',
        rescheduledWithCharge: 'warning'
      }
      return map[status] || 'dark'
    },

    formatDateDisplay(dateStr) {
      if (!dateStr) return ''
      return moment(dateStr, 'YYYY-MM-DD').format('DD/MM/YYYY')
    },

    async exportSummaryToExcel() {
      try {
        const header = ['TIENDA', 'CUENTAS', 'DESDE', 'HASTA', 'TOTAL X ABONAR (S/.)']
        const data = this.clientSummary.map(row => [
          row.clientName,
          row.accounts,
          this.formatDateDisplay(this.filterData.startDate),
          this.formatDateDisplay(this.filterData.endDate),
          row.total.toFixed(2)
        ])

        import('@/vendor/Export2Excel').then(excel => {
          excel.export_json_to_excel_multi_sheet({
            sheets: [
              { sheetName: 'Resumen por Tienda', header, data }
            ],
            filename: 'Resumen por Tienda - Enwam',
            bookType: 'xlsx'
          })
        })
      } catch (e) {
        console.error(e)
        this.$vs.notify({ color: 'warning', title: '¡Oh no!', text: 'Error al exportar.' })
      }
    },

    async exportToExcel() {
      try {
        const sheet1Header = ['FECHA', 'TOTAL DE ENVIOS', 'ENTREGADOS', 'REPROGRAMADOS', 'RECHAZADOS', 'RECHAZADOS CON COBRO', 'ANULADOS', 'ANULADOS CON COBRO', 'EFECTIVIDAD DEL SERVICIO (%)']
        const sheet1Data = Object.entries(this.groupedByDate).map(([date, group]) => [
          this.formatDateDisplay(date),
          group.length,
          this.countByStatus(group, 'done'),
          this.countRescheduled(group),
          this.countByStatus(group, 'rejected'),
          this.countByStatus(group, 'rejectedWithCharge'),
          this.countByStatus(group, 'canceled'),
          this.countByStatus(group, 'canceledWithCharge'),
          this.calcEfficiency(group) + '%'
        ])

        const sheet2Header = ['FECHA', 'COSTO DE RECOJO (s/.10)', 'ADICIONALES (s/.)', 'TARIFA TOTAL (s/.)', 'EN CUENTAS ENWAM (s/.)', 'EN EFECTIVO (s/.)', 'PAGOS POR SISTEMA (s/.)', 'TOTAL (S/.)', 'ESTADO']
        const sheet2Data = Object.entries(this.groupedByDate).map(([date, group]) => {
          const f = this.financeOf(group, date)
          return [
            this.formatDateDisplay(date),
            f.costoRecojo,
            f.adicionales,
            f.tarifaTotal,
            f.enCuentasEnwam,
            f.enEfectivo,
            f.pagosSistema,
            f.total,
            this.getEstadoText(f.total)
          ]
        })

        const sheet3Header = ['COD TRACKING', 'FECHA', 'TIENDA', 'CLIENTE', 'TIPO DE SERVICIO', 'DISTRITO', 'COSTO DE RECOJO', 'ADICIONAL O PAGO EN AGENCIA', 'TARIFA', 'TOTAL A COBRAR', 'DETALLE DE COBRO', 'TIPO DE PAGO', 'INGRESO A CUENTAS', 'EFECTIVO', 'ESTADO']
        const sheet3Data = this.list.map(o => [
          o.correlative,
          this.formatDateDisplay(o.pickUpDate),
          o.client ? o.client.name : '',
          o.fullName,
          o.type ? o.type.label : '',
          o.district ? o.district.label : '',
          this.getCostoRecojoPorEnvio(o),
          o.extraCost > 0 ? o.extraCost : '',
          'S/.' + ((o.district ? o.district.price || 0 : 0) + (o.type ? o.type.price || 0 : 0)).toFixed(2),
          o.totalToBeCollected > 0 ? o.totalToBeCollected : '',
          this.getDetalleCobro(o),
          this.getTipoPago(o),
          'S/.' + this.getIngresoACuentas(o).toFixed(2),
          'S/.' + this.getEfectivo(o).toFixed(2),
          this.getStatusText(o.status)
        ])

        import('@/vendor/Export2Excel').then(excel => {
          excel.export_json_to_excel_multi_sheet({
            sheets: [
              { sheetName: 'Reporte de Envios', header: sheet1Header, data: sheet1Data },
              { sheetName: 'Reporte de Finanzas', header: sheet2Header, data: sheet2Data },
              { sheetName: 'Reporte Detallado', header: sheet3Header, data: sheet3Data }
            ],
            filename: 'Reporte de envíos Enwam',
            bookType: 'xlsx'
          })
        })
      } catch (e) {
        console.error(e)
        this.$vs.notify({ color: 'warning', title: '¡Oh no!', text: 'Error al exportar.' })
      }
    }
  }
}
</script>
